import Button from '@/components/base/Button';
import { formatMoney } from '@/utils/format';
import type { FundingWallet } from '@/types/portal';

interface GoodsConfirmStepProps {
  mark: string;
  till: string;
  merchantName: string;
  amount: number;
  currency: string;
  transactionCost: number;
  interestFee: number;
  total: number;
  wallet: FundingWallet | null;
  canApprove: boolean;
  onPay: () => void;
}

/**
 * "Pay to Buy Goods" confirmation — a read-only receipt of the merchant till
 * payment before it is submitted for settlement or approval.
 */
export default function GoodsConfirmStep({
  mark,
  till,
  merchantName,
  amount,
  currency,
  transactionCost,
  interestFee,
  total,
  wallet,
  canApprove,
  onPay,
}: GoodsConfirmStepProps) {
  const money = currency || 'KES';

  const rows: { label: string; value: string }[] = [
    { label: 'Till Number', value: till || '—' },
    { label: 'Pay to', value: merchantName },
    { label: 'Amount', value: formatMoney(amount, money) },
    {
      label: 'Transaction cost',
      value: transactionCost > 0 ? formatMoney(transactionCost, money) : 'N/A',
    },
    { label: 'Interest Fee', value: formatMoney(interestFee, money) },
  ];

  return (
    <div className="mx-auto w-full max-w-xl">
      <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
        {/* Badge header */}
        <div className="flex flex-col items-center gap-3 border-b border-background-200 bg-secondary-50/70 px-6 py-7">
          <span className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-background-50 bg-accent-100 text-xl font-semibold text-accent-800">
            {mark}
          </span>
          <p className="font-heading text-base font-semibold text-foreground-950">Pay to Buy Goods</p>
        </div>

        {/* Detail rows */}
        <dl className="divide-y divide-background-100 px-6">
          {rows.map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-4 py-3.5">
              <dt className="text-xs font-label text-foreground-500">{row.label}</dt>
              <dd className="truncate text-sm font-medium text-foreground-950">{row.value}</dd>
            </div>
          ))}
          <div className="flex items-center justify-between gap-4 py-4">
            <dt className="text-sm font-medium text-foreground-800">Total debit</dt>
            <dd className="font-heading text-lg font-semibold text-foreground-950">
              {formatMoney(total, money)}
            </dd>
          </div>
        </dl>
      </div>

      <div className="mt-4 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-3 text-xs text-foreground-600">
        <i className="ri-wallet-3-line" />
        Paid from {wallet ? `${wallet.currency} float` : 'your wallet'}
      </div>

      <div
        className={`mt-4 flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm ${
          canApprove
            ? 'border-accent-200 bg-accent-50 text-accent-800'
            : 'border-secondary-200 bg-secondary-50 text-secondary-900'
        }`}
      >
        <i className={`mt-0.5 ${canApprove ? 'ri-shield-check-line' : 'ri-hourglass-line'}`} />
        <span>
          {canApprove
            ? 'As an approver, confirming debits the wallet and pays the merchant immediately.'
            : 'Your confirmation submits this payment for Finance approval. No funds move until an approver signs off.'}
        </span>
      </div>

      <Button icon="ri-check-double-line" size="lg" onClick={onPay} className="mt-5 w-full">
        Pay
      </Button>
    </div>
  );
}